import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { invokeEdgeFunction } from '@/lib/edgeFunctions'

/**
 * Schwab OAuth callback landing page.
 * Schwab redirects here (?code=...) after the user approves. We exchange the code for
 * tokens via the schwab-auth-connect Edge Function, then bounce back to the watchlist.
 */
export function SchwabCallbackPage() {
  const navigate = useNavigate()
  const [state, setState] = useState<'working' | 'success' | 'error'>('working')
  const [message, setMessage] = useState('Connecting your Schwab account...')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const err = params.get('error')

    if (err) {
      setState('error')
      setMessage(`Schwab returned an error: ${err}`)
      return
    }
    if (!code) {
      setState('error')
      setMessage('No authorization code found in the redirect. Please try reconnecting.')
      return
    }

    ;(async () => {
      try {
        const res = await invokeEdgeFunction<{ ok?: boolean; error?: string }>('schwab-auth-connect', {
          params: { action: 'exchange', code },
        })
        if (res.error) {
          setState('error')
          setMessage(res.error)
        } else {
          setState('success')
          setMessage('Schwab connected successfully. Redirecting...')
          setTimeout(() => navigate('/'), 1800)
        }
      } catch (e) {
        setState('error')
        setMessage((e as Error).message || 'Failed to connect Schwab.')
      }
    })()
  }, [navigate])

  return (
    <div className="flex h-full items-center justify-center bg-white p-8">
      <div className="max-w-md text-center">
        {state === 'working' && (
          <>
            <div className="mb-4 text-4xl animate-pulse">🔐</div>
            <h1 className="text-lg font-bold text-gray-900">Connecting Schwab</h1>
            <p className="mt-2 text-sm text-gray-600">{message}</p>
          </>
        )}
        {state === 'success' && (
          <>
            <div className="mb-4 text-4xl">✅</div>
            <h1 className="text-lg font-bold text-green-700">Schwab Connected</h1>
            <p className="mt-2 text-sm text-gray-600">{message}</p>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="mb-4 text-4xl">⚠️</div>
            <h1 className="text-lg font-bold text-red-700">Connection Failed</h1>
            <p className="mt-2 text-sm text-gray-600">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-4 py-2 text-sm rounded bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
            >
              Back to Watchlist
            </button>
          </>
        )}
      </div>
    </div>
  )
}
